// absolute-datetime.ts
// PureDate: Clase para "fechas flotantes" sin zona horaria, implementación nativa
// Compatibles con React Native (sin <input type="datetime-local">).

/* =========================================
   TIPOS Y CONSTANTES
   ========================================= */

/** Formato canónico de string flotante (segundos de precisión). */
export const PURE_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss' as const

/** String flotante canónico (p.ej. "2025-08-26T10:32:45"). */
export type PureDateString = string & { __brand: 'PureDateString' }

/* =========================================
   FUNCIONES INTERNAS
   ========================================= */

/** Días del mes (sí usa Date para el cómputo de días por mes, no para zonas). */
function daysInMonth(y: number, m: number): number {
	return new Date(y, m, 0).getDate()
}

/** Regex de forma (no valida días por mes). */
const ISO_LOCAL_SEC_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/

const ISO_LOCAL_MIN_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/

/** Regex para formato YYYYMMDD */
const COMPACT_DATE_REGEX = /^\d{8}$/

/** Regex para formato YYYY/MM/DD */
const SLASH_DATE_REGEX = /^\d{4}\/\d{2}\/\d{2}$/

/** Regex para formato YYYY-MM-DD */
const DASH_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

/** Detecta el formato de la fecha de entrada */
function detectDateFormat(input: string): 'iso' | 'compact' | 'slash' | 'dash' | 'unknown' {
	if (ISO_LOCAL_SEC_REGEX.test(input)) return 'iso'
	if (ISO_LOCAL_MIN_REGEX.test(input)) return 'iso'
	if (COMPACT_DATE_REGEX.test(input)) return 'compact'
	if (SLASH_DATE_REGEX.test(input)) return 'slash'
	if (DASH_DATE_REGEX.test(input)) return 'dash'
	return 'unknown'
}

/** Valida rangos lógicos (incluye días por mes y bisiestos). */
function isValidAbsolute(
	year: number,
	month: number,
	day: number,
	hour: number,
	minute: number,
	second: number
): boolean {
	if (year < 1 || year > 9999) return false
	if (month < 1 || month > 12) return false
	if (hour < 0 || hour > 23) return false
	if (minute < 0 || minute > 59) return false
	if (second < 0 || second > 59) return false
	return day >= 1 && day <= daysInMonth(year, month)
}

/* =========================================
   CLASE PRINCIPAL PUREDATE
   ========================================= */

/** Clase para fechas flotantes sin zona horaria */
export class PureDate {
	readonly year: number
	readonly month: number
	readonly day: number
	readonly hour: number
	readonly minute: number
	readonly second: number

	constructor(
		input?:
			| string
			| Date
			| PureDate
			| {
					year: number
					month: number
					day: number
					hour?: number
					minute?: number
					second?: number
			  }
	) {
		if (!input) {
			const now = PureDate.now()
			this.year = now.year
			this.month = now.month
			this.day = now.day
			this.hour = now.hour
			this.minute = now.minute
			this.second = 0 // Los segundos siempre son 0
			return
		}
		if (typeof input === 'string') {
			// TODO: Darle una vuelta a este tema de los Z
			// if (input.includes('Z')) {
			// 	input = input.split('Z')[0]
			// }
			const parsed = this.parseString(input)
			this.year = parsed.year
			this.month = parsed.month
			this.day = parsed.day
			this.hour = parsed.hour
			this.minute = parsed.minute
			this.second = parsed.second
		} else if (input instanceof Date) {
			this.year = input.getFullYear()
			this.month = input.getMonth() + 1
			this.day = input.getDate()
			this.hour = input.getHours()
			this.minute = input.getMinutes()
			this.second = 0 // Los segundos siempre son 0
		} else if (input instanceof PureDate) {
			this.year = input.year
			this.month = input.month
			this.day = input.day
			this.hour = input.hour
			this.minute = input.minute
			this.second = 0 // Los segundos siempre son 0
		} else {
			this.year = input.year
			this.month = input.month
			this.day = input.day
			this.hour = input.hour ?? 0
			this.minute = input.minute ?? 0
			this.second = 0 // Los segundos siempre son 0
		}

		// Validar que la fecha sea válida
		if (!isValidAbsolute(this.year, this.month, this.day, this.hour, this.minute, this.second)) {
			throw new Error('Fecha inválida')
		}
	}

	/** Parse de formato compacto YYYYMMDD */
	private parseCompactDate(
		input: string,
		defaultHour: number = 0,
		defaultMinute: number = 0,
		defaultSecond: number = 0
	) {
		if (!COMPACT_DATE_REGEX.test(input)) {
			throw new Error('Formato compacto inválido. Debe ser YYYYMMDD')
		}

		const year = parseInt(input.substring(0, 4), 10)
		const month = parseInt(input.substring(4, 6), 10)
		const day = parseInt(input.substring(6, 8), 10)

		return {
			year,
			month,
			day,
			hour: defaultHour,
			minute: defaultMinute,
			second: defaultSecond,
		}
	}

	/** Parse de formato con slash YYYY/MM/DD */
	private parseSlashDate(
		input: string,
		defaultHour: number = 0,
		defaultMinute: number = 0,
		defaultSecond: number = 0
	) {
		if (!SLASH_DATE_REGEX.test(input)) {
			throw new Error('Formato con slash inválido. Debe ser YYYY/MM/DD')
		}

		const [yearStr = '0', monthStr = '0', dayStr = '0'] = input.split('/')
		const year = parseInt(yearStr, 10)
		const month = parseInt(monthStr, 10)
		const day = parseInt(dayStr, 10)

		return {
			year,
			month,
			day,
			hour: defaultHour,
			minute: defaultMinute,
			second: defaultSecond,
		}
	}

	/** Parse de formato con dash YYYY-MM-DD */
	private parseDashDate(
		input: string,
		defaultHour: number = 0,
		defaultMinute: number = 0,
		defaultSecond: number = 0
	) {
		if (!DASH_DATE_REGEX.test(input)) {
			throw new Error('Formato con dash inválido. Debe ser YYYY-MM-DD')
		}

		const [yearStr = '0', monthStr = '0', dayStr = '0'] = input.split('-')
		const year = parseInt(yearStr, 10)
		const month = parseInt(monthStr, 10)
		const day = parseInt(dayStr, 10)

		return {
			year,
			month,
			day,
			hour: defaultHour,
			minute: defaultMinute,
			second: defaultSecond,
		}
	}

	/** Parse de formato ISO completo YYYY-MM-DDTHH:mm:ss */
	private parseIsoDate(input: string) {
		const [datePart, timePart] = input.split('T')
		if (!datePart || !timePart) {
			throw new Error('Formato ISO inválido: falta separador T')
		}

		const [yearStr, monthStr, dayStr] = datePart.split('-')
		const [hourStr, minuteStr, secondStr = '00'] = timePart.split(':')

		if (!yearStr || !monthStr || !dayStr || !hourStr || !minuteStr) {
			throw new Error('Fecha ISO incompleta')
		}

		const year = parseInt(yearStr, 10)
		const month = parseInt(monthStr, 10)
		const day = parseInt(dayStr, 10)
		const hour = parseInt(hourStr, 10)
		const minute = parseInt(minuteStr, 10)
		const second = parseInt(secondStr, 10)

		return { year, month, day, hour, minute, second }
	}

	/** Parse flexible de string que detecta automáticamente el formato */
	private parseString(input: string) {
		const format = detectDateFormat(input)

		switch (format) {
			case 'iso':
				return this.parseIsoDate(input)
			case 'compact':
				return this.parseCompactDate(input)
			case 'slash':
				return this.parseSlashDate(input)
			case 'dash':
				return this.parseDashDate(input)
			default:
				throw new Error(
					`Formato de fecha no reconocido: ${input}. Formatos soportados: YYYY-MM-DDTHH:mm:ss, YYYYMMDD, YYYY/MM/DD, YYYY-MM-DD`
				)
		}
	}

	/* =========================================
	   MÉTODOS DE INSTANCIA
	   ========================================= */

	/** Obtiene una nueva instancia con los mismos valores */
	get(): PureDate {
		return new PureDate(this)
	}

	/** Convierte a string en formato canónico */
	toString(): PureDateString {
		const pad2 = (n: number) => String(n).padStart(2, '0')
		const s = `${String(this.year).padStart(4, '0')}-${pad2(this.month)}-${pad2(this.day)}T${pad2(this.hour)}:${pad2(this.minute)}:${pad2(this.second)}`
		return s as PureDateString
	}

	toSpanishDate(): string {
		return `${String(this.day).padStart(2, '0')}/${String(this.month).padStart(2, '0')}/${this.year}`
	}

	toSpanishDateTime(): string {
		return `${String(this.day).padStart(2, '0')}/${String(this.month).padStart(2, '0')}/${this.year} ${String(this.hour).padStart(2, '0')}:${String(this.minute).padStart(2, '0')}:${String(this.second).padStart(2, '0')}`
	}

	getDate(): string {
		return `${this.year}-${String(this.month).padStart(2, '0')}-${String(this.day).padStart(2, '0')}`
	}

	getTime(seconds: boolean = false): string {
		if (seconds) {
			return `${String(this.hour).padStart(2, '0')}:${String(this.minute).padStart(2, '0')}:${String(this.second).padStart(2, '0')}`
		}
		return `${String(this.hour).padStart(2, '0')}:${String(this.minute).padStart(2, '0')}`
	}

	getMonthAsShorSpanish(): string {
		return new Intl.DateTimeFormat('es-ES', { month: 'short' }).format(this.toDate())
	}

	/** Convierte a Date en la zona local del dispositivo */
	toDate(): Date {
		return new Date(this.year, this.month - 1, this.day, this.hour, this.minute, this.second, 0)
	}

	/** Obtiene el inicio del día (00:00:00) */
	startOfDay(): PureDate {
		return new PureDate({ ...this, hour: 0, minute: 0, second: 0 })
	}

	/** Obtiene el fin del día (23:59:59) */
	endOfDay(): PureDate {
		return new PureDate({ ...this, hour: 23, minute: 59, second: 59 })
	}

	setTime(hours: number, minutes: number, seconds: number = 0): PureDate {
		return new PureDate({
			...this,
			hour: hours,
			minute: minutes,
			second: seconds,
		})
	}

	setSeconds(seconds: number): PureDate {
		return new PureDate({
			...this,
			second: seconds,
		})
	}

	/** Suma/resta segundos */
	addSeconds(delta: number): PureDate {
		let { year, month, day, hour, minute, second } = this

		let total = hour * 3600 + minute * 60 + second + delta

		// Retrocesos de día
		while (total < 0) {
			day -= 1
			if (day < 1) {
				month -= 1
				if (month < 1) {
					month = 12
					year -= 1
					if (year < 1) throw new Error('Underflow de fecha')
				}
				day = daysInMonth(year, month)
			}
			total += 86400 // 24 * 60 * 60
		}

		// Avances de día
		while (total >= 86400) {
			day += 1
			const dim = daysInMonth(year, month)
			if (day > dim) {
				day = 1
				month += 1
				if (month > 12) {
					month = 1
					year += 1
					if (year > 9999) throw new Error('Overflow de fecha')
				}
			}
			total -= 86400
		}

		hour = Math.floor(total / 3600)
		minute = Math.floor((total % 3600) / 60)
		second = total % 60

		return new PureDate({ year, month, day, hour, minute, second })
	}

	/** Suma/resta minutos */
	addMinutes(delta: number): PureDate {
		return this.addSeconds(delta * 60)
	}

	/** Suma/resta horas */
	addHours(delta: number): PureDate {
		return this.addSeconds(delta * 3600)
	}

	/** Suma/resta días */
	addDays(delta: number): PureDate {
		return this.addSeconds(delta * 86400)
	}

	/** Compara con otra fecha (-1, 0, 1) */
	compare(other: PureDate): -1 | 0 | 1 {
		const A = [this.year, this.month, this.day, this.hour, this.minute, this.second]
		const B = [other.year, other.month, other.day, other.hour, other.minute, other.second]

		for (let i = 0; i < A.length; i++) {
			if (A[i]! < B[i]!) return -1
			if (A[i]! > B[i]!) return 1
		}
		return 0
	}

	/** Formatea la fecha según un patrón */
	format(pattern: string): string {
		const pad2 = (n: number) => String(n).padStart(2, '0')

		return pattern
			.replace(/YYYY/g, String(this.year).padStart(4, '0'))
			.replace(/MM/g, pad2(this.month))
			.replace(/DD/g, pad2(this.day))
			.replace(/HH/g, pad2(this.hour))
			.replace(/mm/g, pad2(this.minute))
			.replace(/ss/g, pad2(this.second))
	}

	/** Valida si la fecha es válida */
	isValid(): boolean {
		return isValidAbsolute(this.year, this.month, this.day, this.hour, this.minute, this.second)
	}

	/** Calcula la diferencia en segundos desde esta fecha hasta otra */
	diffSeconds(other: PureDate): number {
		return PureDate.diffSeconds(this, other)
	}

	/** Calcula la diferencia en minutos desde esta fecha hasta otra */
	diffMinutes(other: PureDate): number {
		return PureDate.diffMinutes(this, other)
	}

	/** Calcula la diferencia en horas desde esta fecha hasta otra */
	diffHours(other: PureDate): number {
		return PureDate.diffHours(this, other)
	}

	/** Calcula la diferencia en días desde esta fecha hasta otra */
	diffDays(other: PureDate): number {
		return PureDate.diffDays(this, other)
	}

	/** Calcula la diferencia en semanas desde esta fecha hasta otra */
	diffWeeks(other: PureDate): number {
		return PureDate.diffWeeks(this, other)
	}

	/** Calcula la diferencia en meses desde esta fecha hasta otra (aproximado) */
	diffMonths(other: PureDate): number {
		return PureDate.diffMonths(this, other)
	}

	/** Calcula la diferencia en años desde esta fecha hasta otra */
	diffYears(other: PureDate): number {
		return PureDate.diffYears(this, other)
	}

	/* =========================================
	   MÉTODOS ESTÁTICOS
	   ========================================= */

	/** Crea una instancia con la fecha actual */
	static now(): PureDate {
		const d = new Date()
		return new PureDate({
			year: d.getFullYear(),
			month: d.getMonth() + 1,
			day: d.getDate(),
			hour: d.getHours(),
			minute: d.getMinutes(),
		})
	}

	/** Valida si un string tiene formato de fecha válido */
	static isValidString(input: string): boolean {
		return detectDateFormat(input) !== 'unknown'
	}

	/** Detecta el formato de un string */
	static detectFormat(input: string): ReturnType<typeof detectDateFormat> {
		return detectDateFormat(input)
	}

	/** Calcula la diferencia en segundos entre dos fechas */
	static diffSeconds(date1: PureDate, date2: PureDate): number {
		// Para fechas del mismo día, solo calculamos la diferencia de tiempo
		if (date1.year === date2.year && date1.month === date2.month && date1.day === date2.day) {
			const total1 = date1.hour * 3600 + date1.minute * 60 + date1.second
			const total2 = date2.hour * 3600 + date2.minute * 60 + date2.second
			return total2 - total1
		}

		// Para fechas de días diferentes, usamos Date para cálculo preciso
		const date1Obj = new Date(date1.year, date1.month - 1, date1.day, date1.hour, date1.minute, date1.second)
		const date2Obj = new Date(date2.year, date2.month - 1, date2.day, date2.hour, date2.minute, date2.second)

		// Diferencia en milisegundos, convertida a segundos
		return Math.floor((date2Obj.getTime() - date1Obj.getTime()) / 1000)
	}

	/** Calcula la diferencia en minutos entre dos fechas */
	static diffMinutes(date1: PureDate, date2: PureDate): number {
		return this.diffSeconds(date1, date2) / 60
	}

	/** Calcula la diferencia en horas entre dos fechas */
	static diffHours(date1: PureDate, date2: PureDate): number {
		return this.diffSeconds(date1, date2) / 3600
	}

	/** Calcula la diferencia en días entre dos fechas */
	static diffDays(date1: PureDate, date2: PureDate): number {
		return Math.floor(this.diffSeconds(date1, date2) / 86400)
	}

	/** Calcula la diferencia en semanas entre dos fechas */
	static diffWeeks(date1: PureDate, date2: PureDate): number {
		return Math.floor(this.diffDays(date1, date2) / 7)
	}

	/** Calcula la diferencia en meses entre dos fechas (aproximado) */
	static diffMonths(date1: PureDate, date2: PureDate): number {
		const yearDiff = date2.year - date1.year
		const monthDiff = date2.month - date1.month
		return yearDiff * 12 + monthDiff
	}

	/** Calcula la diferencia en años entre dos fechas */
	static diffYears(date1: PureDate, date2: PureDate): number {
		return date2.year - date1.year
	}
}

/* =========================================
   EXPORTACIÓN DEL MÓDULO PD
   ========================================= */

/** Módulo principal PD (PureDate) con la clase y utilidades */
export const PD = {
	// Clase principal
	PureDate,

	// Métodos estáticos como funciones de utilidad
	now: PureDate.now,
	isValidString: PureDate.isValidString,
	detectFormat: PureDate.detectFormat,

	// Métodos de diferencia estáticos
	diffSeconds: PureDate.diffSeconds,
	diffMinutes: PureDate.diffMinutes,
	diffHours: PureDate.diffHours,
	diffDays: PureDate.diffDays,
	diffWeeks: PureDate.diffWeeks,
	diffMonths: PureDate.diffMonths,
	diffYears: PureDate.diffYears,

	// Constructor como función de conveniencia
	create: (input: ConstructorParameters<typeof PureDate>[0]) => new PureDate(input),

	// Métodos de utilidad que crean instancias
	parse: (input: string) => new PureDate(input),
	fromDate: (date: Date) => new PureDate(date),
	fromParts: (parts: { year: number; month: number; day: number; hour?: number; minute?: number; second?: number }) =>
		new PureDate(parts),
}
